import { useState, useCallback, useEffect, useRef } from 'react'
import { TopCard } from './components/TopCard'
import { ChatPanel } from './components/ChatPanel'
import { EventsPanel } from './components/EventsPanel'
import { NetWorthChart } from './components/NetWorthChart'
import { CashFlowChart } from './components/CashFlowChart'
import { ScenarioSummary } from './components/ScenarioSummary'
import { RealityTracker } from './components/RealityTracker'
import { runSimulation, defaultParams } from './engine/montecarlo'
import { loadSavedProfile, saveProfile, clearSavedProfile, loadRememberMe, saveRememberMe } from './utils/storage'
import { exportToJSON, importFromJSON, generateShareURL, readShareFromURL, applySharePayload, clearShareFragment } from './utils/sharing'
import type { SimulationParams, SimulationResult, RealityEntry } from './engine/types'
import './index.css'

function initialParams(): SimulationParams {
  const base = defaultParams()
  const saved = loadSavedProfile()
  if (saved) return { ...base, profile: saved }
  return base
}

export function App() {
  const [params, setParams] = useState<SimulationParams>(initialParams)
  const [result, setResult] = useState<SimulationResult | null>(null)
  const [running, setRunning] = useState(false)
  const [realityEntries, setRealityEntries] = useState<RealityEntry[]>([])
  const [activeTab, setActiveTab] = useState<'simulation' | 'reality'>('simulation')
  const [activeScenario, setActiveScenario] = useState<string | null>(null)
  const [rememberMe, setRememberMe] = useState(() => loadRememberMe())
  const [shareToast, setShareToast] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const autoRunPending = useRef(false)

  // On mount: read share URL and restore params if present
  useEffect(() => {
    const payload = readShareFromURL()
    if (payload) {
      const base = defaultParams()
      const birthYear = payload.p.birthYear || 1985
      const partnerBirthYear = payload.p.partnerBirthYear
      const youngestBirthYear = partnerBirthYear > 0 ? Math.max(birthYear, partnerBirthYear) : birthYear
      const projYears = (youngestBirthYear + 100) - base.startYear
      const userSS = { ...base.userSS, birthYear }
      const spouseSS = partnerBirthYear > 0 ? { ...base.spouseSS, birthYear: partnerBirthYear } : base.spouseSS
      const restored = applySharePayload(base, payload)
      const finalParams = { ...restored, projectionYears: Math.max(projYears, 10), userSS, spouseSS }
      setParams(finalParams)
      setActiveScenario('Shared Scenario')
      clearShareFragment()
      autoRunPending.current = true
    }
  }, []) // mount-only — intentionally captures no deps; payload applied via direct setters

  // Auto-run after share URL restore: fires whenever params changes while flag is set
  useEffect(() => {
    if (!autoRunPending.current) return
    autoRunPending.current = false
    setRunning(true)
    setTimeout(() => {
      try {
        setResult(runSimulation(params))
      } finally {
        setRunning(false)
      }
    }, 10)
  }, [params]) // runs after params state update commits

  function handleRememberMeChange(value: boolean) {
    setRememberMe(value)
    saveRememberMe(value)
    // If user just unchecked, immediately clear any saved data
    if (!value) clearSavedProfile()
  }

  // projectionYears = youngest person lives to 100
  // "youngest" = most recent birth year among user and partner (if set)
  function handleParamsChange(updated: SimulationParams) {
    const birthYear = updated.profile.birthYear || 1985
    const partnerBirthYear = updated.profile.partnerBirthYear
    const youngestBirthYear = partnerBirthYear > 0
      ? Math.max(birthYear, partnerBirthYear)
      : birthYear
    const projYears = (youngestBirthYear + 100) - updated.startYear
    // Sync SS birthYear from profile so SS calc stays consistent
    const userSS = { ...updated.userSS, birthYear }
    const spouseSS = partnerBirthYear > 0
      ? { ...updated.spouseSS, birthYear: partnerBirthYear }
      : updated.spouseSS
    // Auto-set retirement target to 25x expenses when not yet set
    const totalExpenses = updated.profile.annualHousingExpense + updated.profile.annualOtherExpenses
    const suggestedTarget = totalExpenses > 0 ? Math.round(totalExpenses * 25 / 100_000) * 100_000 : 0
    const retirementTargetNetWorth = updated.retirementTargetNetWorth === 0 && suggestedTarget > 0
      ? suggestedTarget
      : updated.retirementTargetNetWorth
    setParams({ ...updated, projectionYears: Math.max(projYears, 10), userSS, spouseSS, retirementTargetNetWorth })
  }

  function handleExport() {
    exportToJSON(params)
  }

  async function handleImport(file: File) {
    setImportError(null)
    try {
      const imported = await importFromJSON(file)
      handleParamsChange(imported)
      setActiveScenario('Imported Scenario')
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed')
      setTimeout(() => setImportError(null), 5000)
    }
  }

  function handleShare() {
    const url = generateShareURL(params)
    navigator.clipboard.writeText(url).then(() => {
      setShareToast('✓ Copied!')
      setTimeout(() => setShareToast(null), 2500)
    }).catch(() => {
      // Clipboard blocked — show the URL for manual copy
      setShareToast('URL ready')
      setTimeout(() => setShareToast(null), 2500)
    })
  }

  const handleRun = useCallback(() => {
    // Persist profile to localStorage if user opted in
    if (rememberMe) {
      saveProfile(params.profile)
    } else {
      clearSavedProfile()
    }
    setRunning(true)
    // Defer to allow React to update the button state before heavy computation
    setTimeout(() => {
      try {
        const res = runSimulation(params)
        setResult(res)
      } finally {
        setRunning(false)
      }
    }, 10)
  }, [params, rememberMe])

  // Derive actual net worth points for reality overlay on charts
  const realityChartData = realityEntries.map(e => ({
    year: e.calendarYear,
    netWorth:
      e.liquidAssets + e.retirementAssets + e.homeEquity,
  }))

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-inner">
          <h1>312-finance</h1>
          <span className="header-sub">Monte Carlo Financial Simulator · Not financial advice</span>
        </div>
        <nav className="tab-nav">
          <button
            className={activeTab === 'simulation' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('simulation')}
          >
            Simulation
          </button>
          <button
            className={activeTab === 'reality' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('reality')}
          >
            Reality Tracker
          </button>
        </nav>
      </header>

      {activeTab === 'simulation' && (
        <>
          <TopCard
            params={params}
            onChange={handleParamsChange}
            onRun={handleRun}
            running={running}
            rememberMe={rememberMe}
            onRememberMeChange={handleRememberMeChange}
            onExport={handleExport}
            onImport={handleImport}
            onShare={handleShare}
            shareToast={shareToast}
          />

          <div className="sim-layout">
            <ChatPanel
              params={params}
              onParamsChange={handleParamsChange}
              activeScenario={activeScenario}
              onScenarioChange={setActiveScenario}
              onRunSimulation={handleRun}
            />

            <main className="sim-main">
              {importError && (
                <div className="import-error-banner">⚠ {importError}</div>
              )}
              {activeScenario && (
                <div className="scenario-indicator">
                  <span className="scenario-indicator-dot" />
                  <strong>Scenario:</strong> {activeScenario}
                  <button className="scenario-indicator-clear" onClick={() => setActiveScenario(null)}>✕</button>
                </div>
              )}
              {!result && !running && (
                <div className="empty-state">
                  <p>Fill in <strong>About You</strong>, then click <strong>▶ Run</strong> or ask the AI to simulate a scenario.</p>
                </div>
              )}
              {running && (
                <div className="empty-state">
                  <p>Running {params.iterations.toLocaleString()} iterations…</p>
                </div>
              )}
              {result && !running && (
                <>
                  <ScenarioSummary result={result} startYear={params.startYear} retirementTarget={params.retirementTargetNetWorth} inflationMean={params.inflation.mean} />
                  <NetWorthChart result={result} retirementTarget={params.retirementTargetNetWorth} realityData={realityChartData} inflationMean={params.inflation.mean} startYear={params.startYear} />
                  <CashFlowChart result={result} inflationMean={params.inflation.mean} startYear={params.startYear} />
                </>
              )}
            </main>

            <EventsPanel
              events={params.events ?? []}
              onChange={events => handleParamsChange({ ...params, events })}
              startYear={params.startYear}
              profile={params.profile}
              params={params}
              onParamsChange={handleParamsChange}
              onRunSimulation={handleRun}
              onScenarioChange={setActiveScenario}
            />
          </div>
        </>
      )}

      {activeTab === 'reality' && (
        <div className="reality-layout">
          <RealityTracker onDataChange={setRealityEntries} />
          {result && realityEntries.length > 0 && (
            <NetWorthChart
              result={result}
              retirementTarget={params.retirementTargetNetWorth}
              realityData={realityChartData}
              inflationMean={params.inflation.mean}
              startYear={params.startYear}
            />
          )}
          {(!result || realityEntries.length === 0) && (
            <p className="reality-hint">
              {!result
                ? 'Run a simulation first to see actuals overlaid on projections.'
                : 'Add entries above to see them plotted against your simulation.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
